import { IAttrValue, OWDataType } from "@multiweb/types/contracts";
import { InvalidArgumentException } from "../../../Global/Base/Exceptions";
import { IComparable } from "../../../Global/Base/IComparable";
import { NumberUtils } from "../../../Global/Base/Utils/NumberUtils";
import { CultureInfo } from "../../../Global/Dom/CultureInfo";
import { IVariant, VariantType } from "./IVariant";
/**
 * Datový typ reprezentující strukturu, která může uchovávat hodnotu různých datových typů. V současnosti reprezentuje aplikační typ StrNum a umí uchovávat hodnoty typu number a string.
 * @includeToDoc
*/
export class OWVariant implements IVariant, IAttrValue, IComparable {
    /** Typ uchovávané hodnoty */
    public readonly type: VariantType;
    /** Uchovávaná hodnota */
    public readonly value: any;
    /** Počet desetinných čísel pro formátování číselných hodnot */
    public readonly decimalCount?: number;

    constructor(value: IVariant);
    constructor(value: string);
    constructor(value: number, decimalCount?: number);
    constructor(value: any, decimalCount?: number) {
        if (value == null)
            throw new InvalidArgumentException();

        if (typeof value === "number") {
            if (Number.isNaN(value))
                throw new InvalidArgumentException();

            this.type = VariantType.number;
            this.value = value;
            if (decimalCount != null)
                this.decimalCount = decimalCount;
            return;
        }

        if (typeof value === "string") {
            const matchRes = /^(-?\d+([eE]-?\d+)?(\.\d+)?)#(\d*)$/.exec(value);
            if (matchRes == null) {
                this.type = VariantType.string;
                this.value = value;
                return;
            }

            const [, numPart, , , decimalPart] = matchRes;
            const num = Number.parseFloat(numPart);
            if (Number.isNaN(num)) {
                this.type = VariantType.string;
                this.value = value;
                return;
            }

            this.type = VariantType.number;
            this.value = num;

            const decimals = Number.parseInt(decimalPart);
            if (!Number.isNaN(decimals))
                this.decimalCount = decimals;

            return;
        }

        if (value instanceof IVariant) {
            this.type = value.type;
            this.value = value.value;
            this.decimalCount = value.decimalCount;
            return;
        }

        throw new InvalidArgumentException(`Value '${value}' is not recognized as a Variant`);
    }

    /**
     * Získá uloženou primitivní hodnotu. Pro čísla vrátí číslo, pro text vrátí string.
     */
    public valueOf(): any {
        return this.value;
    }

    private _getDefaultNumberFormat(): Readonly<Intl.NumberFormatOptions> {
        return NumberUtils.getStandardNumberFormatOptions(NumberUtils.getDefaultFormat(OWDataType.double));
    }

    /**
     * Převede strukturu na textovou reprezentaci.
     * U textu vrátí nezměněný text.
     * U čísla ho naformátuje podle výchozího formátu pro reálná čísla a současné kultury.
     * Pokud je specifikován počet desetinných míst, tak při formátování uplatní.
     */
    public toString(): string {
        return this.toLocaleString(CultureInfo.getCulture());
    }

    /**
     * Převede strukturu na textovou reprezentaci.
     * U textu vrátí nezměněný text.
     * U čísla ho naformátuje podle výchozího formátu pro reálná čísla a současné kultury.
     * Formát lze předat vlastní jako volitelný parametr.
     * Pokud je specifikován počet desetinných míst, tak při formátování uplatní.
     * @param locales Volitelný jazyk pro formátování číselné hodnoty
     * @param options Volitelný formát pro formátování číselné hodnoty
     */
    public toLocaleString(locales?: string | string[], options?: Intl.NumberFormatOptions): string {
        if (this.type === VariantType.string)
            return this.value as string;

        if (!options)
            options = this._getDefaultNumberFormat();

        let usedFormat: Intl.NumberFormatOptions;
        if (this.decimalCount != null) {
            usedFormat = {
                ...options, // zkopírování objektu, ať neměníme ten předaný
                maximumFractionDigits: this.decimalCount,
                minimumFractionDigits: this.decimalCount,
            };
        } else {
            usedFormat = options;
        }

        return (this.value as number).toLocaleString(locales, usedFormat);
    }

    public compare(value: any): boolean {
        if (value instanceof IVariant) {
            return this.type === value.type &&
                this.value === value.value &&
                this.decimalCount === value.decimalCount;
        }
        return false;
    }
}
